import * as React from "react";
import { Button } from "@jhool-io/fe-components";
import { Document, Page } from "react-pdf";
import { PDF_DOCUMENT_OPTIONS } from "../../lib/pdfWorker";
import { cn } from "../../utils/helpers";
import ChevronLeftIcon from "../Icons/ChevronLeft";
import ChevronRightIcon from "../Icons/ChevronRight";
import ListState from "../ListState/ListState";
import Skeleton from "../Skeleton/Skeleton";

const MAX_PAGE_WIDTH = 800;
const VIEWER_HORIZONTAL_PADDING = 32;

export interface DocumentWorkspaceNavigation {
    current: number;
    total: number;
    onPrevious: () => void;
    onNext: () => void;
}

interface DocumentWorkspaceViewerProps {
    documentKey: string;
    data?: string;
    contentType?: string;
    isLoading?: boolean;
    isError?: boolean;
    title: string;
    subtitle?: React.ReactNode;
    badge?: React.ReactNode;
    navigation?: DocumentWorkspaceNavigation;
    onDownload?: () => void;
    isDownloading?: boolean;
    renderSidePanel?: (pageNumber: number) => React.ReactNode;
}

export default function DocumentWorkspaceViewer({
    documentKey,
    data,
    contentType = "application/pdf",
    isLoading,
    isError,
    title,
    subtitle,
    badge,
    navigation,
    onDownload,
    isDownloading,
    renderSidePanel,
}: DocumentWorkspaceViewerProps) {
    const [numPages, setNumPages] = React.useState(0);
    const [pageNumber, setPageNumber] = React.useState(1);
    const [pageWidth, setPageWidth] = React.useState(MAX_PAGE_WIDTH);
    const [zoom, setZoom] = React.useState(1);
    const viewerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        setNumPages(0);
        setPageNumber(1);
        setZoom(1);
    }, [documentKey]);

    React.useEffect(() => {
        const viewer = viewerRef.current;
        if (!viewer || typeof ResizeObserver === "undefined") return undefined;

        const updatePageWidth = (width: number) => {
            const availableWidth = Math.floor(
                width - VIEWER_HORIZONTAL_PADDING
            );
            if (availableWidth > 0) {
                setPageWidth(Math.min(availableWidth, MAX_PAGE_WIDTH));
            }
        };

        updatePageWidth(viewer.clientWidth);
        const resizeObserver = new ResizeObserver(([entry]) => {
            updatePageWidth(entry.contentRect.width);
        });
        resizeObserver.observe(viewer);

        return () => resizeObserver.disconnect();
    }, [isError, isLoading]);

    const goToPage = (page: number) => {
        if (!numPages) return;
        setPageNumber(Math.min(Math.max(page, 1), numPages));
    };

    if (isLoading) {
        return (
            <Skeleton
                containerTestId="document-workspace-viewer-loader"
                className="h-[500px] w-full"
            />
        );
    }

    if (isError) {
        return (
            <ListState
                isError
                errorMsg="Could not load document at this time"
                stateHelperText="Try reloading the page to fix this error"
            />
        );
    }

    if (!data) return null;

    const isImage = /^image/.test(contentType);

    return (
        <div className="flex h-[calc(100dvh-48px)] min-h-0 flex-col overflow-hidden bg-white">
            <header className="flex shrink-0 flex-col justify-between gap-2 border-b border-strokelight px-12 py-8 sm:flex-row sm:items-center">
                <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                        <h2
                            className="truncate text-base font-semibold text-black"
                            title={title}
                        >
                            {title}
                        </h2>
                        {badge}
                    </div>
                    {subtitle && (
                        <div className="mt-2 truncate text-xs text-gray">
                            {subtitle}
                        </div>
                    )}
                </div>

                <div className="flex shrink-0 items-center justify-end gap-2">
                    {navigation && navigation.total > 1 && (
                        <div className="flex items-center gap-1 rounded-r6 border border-strokelight p-1">
                            <Button
                                type="button"
                                variant="normal"
                                aria-label="Previous document"
                                className="flex size-6 items-center justify-center border-0 bg-transparent p-0 text-gray"
                                onClick={navigation.onPrevious}
                            >
                                <ChevronLeftIcon />
                            </Button>
                            <span className="min-w-16 text-center text-xs font-medium text-gray">
                                {navigation.current} of {navigation.total}
                            </span>
                            <Button
                                type="button"
                                variant="normal"
                                aria-label="Next document"
                                className="flex size-6 items-center justify-center border-0 bg-transparent p-0 text-gray"
                                onClick={navigation.onNext}
                            >
                                <ChevronRightIcon />
                            </Button>
                        </div>
                    )}
                    {onDownload && (
                        <Button
                            variant="secondary"
                            mode="outline"
                            onClick={onDownload}
                            disabled={isDownloading}
                        >
                            {isDownloading ? "Downloading..." : "Download"}
                        </Button>
                    )}
                </div>
            </header>

            <div
                className={cn(
                    "grid min-h-0 flex-1 grid-cols-1 overflow-y-auto",
                    renderSidePanel &&
                        "lg:grid-cols-[minmax(0,1fr)_280px] lg:overflow-hidden"
                )}
            >
                <section className="flex min-h-[420px] min-w-0 flex-col bg-[#E9EDF2]">
                    {!isImage && (
                        <div className="sticky top-0 z-10 flex min-h-48 shrink-0 items-center justify-center gap-12 overflow-x-auto border-b border-[#D7DDE5] bg-white/95 px-12 py-4 shadow-sm backdrop-blur">
                            <div className="flex shrink-0 items-center gap-8 rounded-r8 border border-stroke bg-white p-4">
                                <Button
                                    type="button"
                                    variant="normal"
                                    aria-label="Previous page"
                                    className="flex size-32 shrink-0 items-center justify-center rounded-r6 border-0 bg-transparent p-0 text-gray hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
                                    disabled={pageNumber <= 1}
                                    onClick={() => goToPage(pageNumber - 1)}
                                >
                                    <ChevronLeftIcon />
                                </Button>
                                <span className="min-w-[96px] whitespace-nowrap text-center text-sm font-semibold text-black">
                                    Page {pageNumber} of {numPages || "—"}
                                </span>
                                <Button
                                    type="button"
                                    variant="normal"
                                    aria-label="Next page"
                                    className="flex size-32 shrink-0 items-center justify-center rounded-r6 border-0 bg-transparent p-0 text-gray hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
                                    disabled={
                                        !numPages || pageNumber >= numPages
                                    }
                                    onClick={() => goToPage(pageNumber + 1)}
                                >
                                    <ChevronRightIcon />
                                </Button>
                            </div>
                            <div className="flex shrink-0 items-center gap-8 rounded-r8 border border-stroke bg-white p-4">
                                <Button
                                    type="button"
                                    variant="normal"
                                    aria-label="Zoom out"
                                    className="flex size-32 shrink-0 items-center justify-center rounded-r6 border-0 bg-transparent p-0 text-base text-gray hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
                                    disabled={zoom <= 0.75}
                                    onClick={() =>
                                        setZoom((value) =>
                                            Math.max(0.75, value - 0.25)
                                        )
                                    }
                                >
                                    −
                                </Button>
                                <span className="min-w-[48px] whitespace-nowrap text-center text-xs font-medium text-gray">
                                    {Math.round(zoom * 100)}%
                                </span>
                                <Button
                                    type="button"
                                    variant="normal"
                                    aria-label="Zoom in"
                                    className="flex size-32 shrink-0 items-center justify-center rounded-r6 border-0 bg-transparent p-0 text-base text-gray hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
                                    disabled={zoom >= 1.75}
                                    onClick={() =>
                                        setZoom((value) =>
                                            Math.min(1.75, value + 0.25)
                                        )
                                    }
                                >
                                    +
                                </Button>
                            </div>
                        </div>
                    )}

                    <div
                        ref={viewerRef}
                        className="relative flex min-h-0 flex-1 items-start overflow-auto p-8 sm:p-16 lg:p-24"
                    >
                        {isImage ? (
                            <img
                                src={`data:${contentType};base64,${data}`}
                                alt={title}
                                className="m-auto max-h-full max-w-full object-contain shadow-lg"
                            />
                        ) : (
                            <div
                                className="mx-auto shrink-0 shadow-[0_12px_32px_rgba(15,23,42,0.18)]"
                                style={{ width: Math.round(pageWidth * zoom) }}
                            >
                                <Document
                                    file={`data:application/pdf;base64,${data}`}
                                    options={PDF_DOCUMENT_OPTIONS}
                                    loading={null}
                                    error={
                                        <span className="flex h-32 items-center justify-center bg-white px-24 text-danger">
                                            Could not load this PDF
                                        </span>
                                    }
                                    onLoadSuccess={({ numPages: count }) => {
                                        setNumPages(count);
                                        setPageNumber(1);
                                    }}
                                >
                                    <Page
                                        pageNumber={pageNumber}
                                        width={pageWidth}
                                        scale={zoom}
                                        renderAnnotationLayer={false}
                                        renderTextLayer={false}
                                    />
                                </Document>
                            </div>
                        )}
                    </div>
                </section>

                {renderSidePanel && (
                    <aside className="overflow-y-auto border-t border-strokelight bg-white p-16 lg:border-l lg:border-t-0">
                        {renderSidePanel(pageNumber)}
                    </aside>
                )}
            </div>
        </div>
    );
}
